<script>
import { formatNumber } from '@utils/Format'
import {
  CREDITOS as CREDITOS_BUS,
  FEATURES,
  MODAL,
  RELOAD,
} from '@constants/Text'
import { GlobalBus } from '@services/bus'
import { CREDITOS } from '@modules/ai-framework/constants/copy'
import {
  CASAS_EXIBICAO,
  saldoBaixo,
  saldoEsgotado,
} from '@modules/ai-framework/utils/creditos'
import { apiAiFrameworkCreditos } from '@modules/ai-framework/api/apiAiFrameworkCreditos'

// Atalho do saldo no RODAPÉ da lateral do chat (home por IA). O consumo de créditos
// acontece lá — é conversando que o consultor gasta —, e sem este atalho o número só
// existiria em `/central-ia/creditos`, na tela que ele não está olhando; descobrir que o
// crédito acabou no meio de um atendimento é o pior momento possível.
//
// ★ O clique abre o MODAL de créditos (`MODAL.AI_FRAMEWORK_CREDITOS`, montado em
// `global-modals.vue`) em vez de navegar: o consultor vê o saldo, compra e volta sem
// perder a conversa. A rota cheia continua existindo (deep-link, retorno da Stripe).
//
// ★ Por que um componente próprio, e não o `saldo-creditos.vue`: o card grande é uma
// REGIÃO (header, KPI, equivalência em reais, bloco de ajuda do negativo, carimbo de
// atualização) — seis estados em ~90px de altura. Aqui cabe UMA linha. Espremer o card
// entregaria os dois mal; o que se reusa é o que precisa ser único: a store
// (`aiFramework/creditos`), a copy e a regra do que conta como negativo.
//
// ★ A anatomia visual vem de `.ia-sidebar-row` (`aawz-intelligence-v2/design/
// _ia-sidebar.sass`), o dono declarado da linha clicável desta superfície — padding,
// gap, raio, hover, o passo de 13px e o contrato de contraste sobre o fundo da lateral
// moram lá e em nenhum `<style scoped>`. Mesmo precedente de `.ia-block-card` sendo
// consumida por `crm/ia/crm-conversas-whatsapp-card.vue`.
const STORE = 'aiFramework/creditos'
const TAMANHO_ICONE = 16
// Mesmo tom dos outros ícones da lateral (`COR_ICONE` em `chat-sidebar.vue`).
const COR_ICONE = 'var(--neutral-700)'
// #C03939 sobre a superfície da lateral (#F4F4F4): 5.05 — passa AA para texto normal.
// O `--danger` puro (#EB5757) dá 3.4 e reprovaria no rótulo ao lado.
const COR_ICONE_NEGATIVO = 'var(--danger-text)'
// Saldo baixo/esgotado: o âmbar exportado como custom property (`--warning-new` é só
// variável SASS e não resolve em `var()` — armadilha registrada em `ia-insight-card/tons.js`).
const COR_ALERTA = 'var(--warning-text)'
const TAMANHO_ICONE_ALERTA = 12
// Quanto tempo o botão pulsa depois de o chat avisar que o saldo esgotou (402): 3 pulsos
// de 800ms — a mesma conta de `.iv2-chamar-atencao` em `aawz-intelligence-v2/design/efeitos.sass`.
const DURACAO_CHAMAR_ATENCAO_MS = 2400
const CLASSE_CHAMAR_ATENCAO = 'iv2-chamar-atencao'

export default {
  name: 'AiFrameworkSaldoCreditosRodape',
  props: {
    // Recolhida, a lateral vira régua de ícones (64px): o rodapé acompanha em vez de
    // sumir — some o texto, fica o ícone, e o nome acessível continua completo.
    recolhida: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      copy: CREDITOS,
      TAMANHO_ICONE,
      TAMANHO_ICONE_ALERTA,
      corAlerta: COR_ALERTA,
      // O chat acabou de ser recusado por saldo: o botão pulsa até o timer soltar.
      chamandoAtencao: false,
    }
  },
  computed: {
    // Mesmo gate da rota (`ai-framework/routes.js` → `checkPermission`): sem a feature o
    // escritório não tem créditos, e o modal abriria sobre um endpoint que não existe.
    temFeature() {
      const show = this.$store.getters['parameters/show'] || {}
      return Boolean(show[FEATURES.AI_FRAMEWORK])
    },
    forbidden() {
      return Boolean(this.$store.getters[STORE + '/saldoForbidden'])
    },
    // Sem permissão o atalho INTEIRO sai, em vez de repetir a frase do card: numa coluna
    // de navegação, um parágrafo de permissão ocupa o lugar de três conversas para dizer
    // que não há nada a ver. Quem chegar à tela de créditos por outro caminho lê a
    // explicação completa lá.
    visivel() {
      return this.temFeature && !this.forbidden
    },
    loading() {
      return Boolean(this.$store.getters[STORE + '/saldoLoading'])
    },
    saldo() {
      return this.$store.getters[STORE + '/saldoCreditos']
    },
    // Valor exato como veio do backend (string de 6 casas, ADR-007) — é ele que vai para
    // o DOM; o arredondado é só o que se lê.
    creditosRaw() {
      const bruto = this.saldo && this.saldo.creditos
      if (bruto === undefined || bruto === null) return ''
      return String(bruto)
    },
    creditosNumero() {
      if (!this.creditosRaw) return null
      const numero = Number(this.creditosRaw)
      return isFinite(numero) ? numero : null
    },
    // ★ Sem número confirmado NÃO se escreve "0,00": zero inventado é indistinguível de
    // saldo zerado de verdade, e é exatamente essa a informação que decide se o
    // consultor continua. Mesma regra do card grande.
    mostrarIndisponivel() {
      return !this.loading && this.creditosNumero === null
    },
    creditosLabel() {
      if (this.creditosNumero === null) return ''
      return formatNumber(this.creditosNumero, CASAS_EXIBICAO)
    },
    // A flag do backend manda; sem ela, o próprio sinal decide — o atalho nunca depende
    // de um campo opcional para acertar o estado que mais importa.
    negativo() {
      if (this.saldo && this.saldo.negativo === true) return true
      return this.creditosNumero !== null && this.creditosNumero < 0
    },
    // Terceiro canal do estado negativo, ao lado da cor e da FORMA do ícone (triângulo):
    // o leitor de tela recebe a palavra "negativo", não a cor. ADR-010 — o saldo
    // negativo não bloqueia nada, então aqui não há alerta nem bloqueio, só o estado.
    iconeNome() {
      return this.negativo ? 'warning-status' : 'wallet'
    },
    iconeCor() {
      return this.negativo ? COR_ICONE_NEGATIVO : COR_ICONE
    },
    // ★ Saldo BAIXO (`0 < saldo <= LIMIAR_SALDO_BAIXO`) ou ESGOTADO (zero): um tom só
    // (warning) para os dois — zero é mais grave, mas um terceiro estado só confundiria.
    // O negativo NÃO entra: já tem o tratamento próprio (danger, ADR-010) nos três canais.
    // Expandida: número em âmbar + triângulo ao lado; recolhida: ponto no canto do ícone.
    alertaDeSaldo() {
      if (this.negativo) return false
      return saldoBaixo(this.creditosRaw) || saldoEsgotado(this.creditosRaw)
    },
    // Recolhida, o botão é o MESMO quadrado dos ícones da régua
    // (`ia-sidebar-botao-icone`, receita única em `design/_ia-sidebar.sass`);
    // expandida, é a linha da lateral com rótulo e valor.
    classeDoAtalho() {
      return {
        'saldo-rodape__atalho': true,
        'ia-sidebar-botao-icone': this.recolhida,
        'ia-sidebar-row': !this.recolhida,
        [CLASSE_CHAMAR_ATENCAO]: this.chamandoAtencao,
      }
    },
    valorFalado() {
      return this.creditosLabel + ' ' + CREDITOS.UNIDADE
    },
    ariaLabel() {
      if (this.creditosNumero === null) return CREDITOS.RODAPE_ATALHO
      const saldoFalado = this.negativo
        ? CREDITOS.ARIA_SALDO_NEGATIVO(this.valorFalado)
        : CREDITOS.ARIA_SALDO(this.valorFalado)
      return CREDITOS.RODAPE_ARIA(saldoFalado)
    },
    // Tooltip nativo só na régua recolhida, onde o rótulo e o número não estão na tela.
    // Expandida, repetir em balão o que já está escrito ao lado é ruído.
    tituloAtalho() {
      return this.recolhida ? this.ariaLabel : null
    },
  },
  created() {
    // Sem `idProfissional`: aqui é sempre a própria carteira. Gestor consulta a de outro
    // pela tela de créditos, que tem o seletor — a lateral é o contexto do consultor
    // conversando, e é o saldo DELE que decide se ele continua.
    //
    // Condicionado à feature para não bater num endpoint que o escritório não tem.
    // `forbidden` não serve de guard aqui: ele só é conhecido DEPOIS da resposta.
    if (this.temFeature) {
      this.buscarSaldo()
    }
    // O consumo acontece na tela ao lado: sem isto o número ficaria congelado no
    // valor da abertura enquanto o consultor conversa. Quem avisa é o motor do chat,
    // no FECHO do stream — ver `_avisarQueOSaldoMudou` em `apiChat.js`.
    GlobalBus.$on(RELOAD.CREDITOS_SALDO, this.recarregarSaldo)
    // O turno recusado por saldo (402) aponta para ESTE botão ("clique em Meus
    // créditos, no rodapé da lateral") — ele pulsa para o olho achá-lo. Emissor:
    // `apiChat._desfechoDeSaldoEsgotado`.
    GlobalBus.$on(CREDITOS_BUS.CHAMAR_ATENCAO, this.chamarAtencao)
  },
  beforeDestroy() {
    GlobalBus.$off(RELOAD.CREDITOS_SALDO, this.recarregarSaldo)
    GlobalBus.$off(CREDITOS_BUS.CHAMAR_ATENCAO, this.chamarAtencao)
    this.pararDeChamarAtencao()
  },
  methods: {
    buscarSaldo() {
      apiAiFrameworkCreditos.carregarSaldo({ idProfissional: null })
    },
    // ★ O gate é `visivel`, não `temFeature`: depois de um 403 o rodapé sai da tela, e
    // insistir no endpoint a cada turno seria pedir de novo, resposta após resposta, o
    // que o servidor já recusou — para não mostrar nada.
    recarregarSaldo() {
      if (!this.visivel) return
      this.buscarSaldo()
    },
    // Quem abre é o modal (`creditos-modal.vue`), que faz o próprio `carregarSaldo` — este
    // componente não passa payload nem estado: o canal é o contrato inteiro.
    abrirModal() {
      GlobalBus.$emit(MODAL.AI_FRAMEWORK_CREDITOS)
    },
    // Liga a classe do pulso e agenda a saída. Sinal em cima de sinal REINICIA a
    // contagem (o timer anterior é limpo) — a classe nunca some no meio de um pulso.
    chamarAtencao() {
      this.pararDeChamarAtencao()
      this.chamandoAtencao = true
      this._timerChamarAtencao = setTimeout(() => {
        this.chamandoAtencao = false
        this._timerChamarAtencao = null
      }, DURACAO_CHAMAR_ATENCAO_MS)
    },
    pararDeChamarAtencao() {
      if (!this._timerChamarAtencao) return
      clearTimeout(this._timerChamarAtencao)
      this._timerChamarAtencao = null
    },
  },
}
</script>

<template>
  <div
    v-if="visivel"
    class="saldo-rodape mt-2 pt-2"
    :class="{ 'saldo-rodape--recolhida d-flex justify-center': recolhida }"
    data-test="saldo-creditos-rodape"
  >
    <!-- `<button>`, e não `<router-link>`: o destino é um modal, não uma rota. É também
         o elemento para o qual `.ia-sidebar-row` nasceu — a receita já traz o reset
         (borda, fundo, largura, alinhamento, cursor, cor), então nada se repete aqui. -->
    <button
      type="button"
      :class="classeDoAtalho"
      :title="tituloAtalho"
      :aria-label="ariaLabel"
      data-test="saldo-creditos-rodape-atalho"
      @click="abrirModal"
    >
      <!-- Mesmo par de ícones do card grande e do modal, de propósito: quem chega aos
           créditos por aqui precisa reconhecer o mesmo objeto do outro lado. `wallet`
           no estado normal; `warning-status` (triângulo) no negativo — FORMA distinta é
           o canal que sobrevive a daltonismo e a impressão em preto e branco. -->
      <!-- Recolhida não há número nem triângulo ao lado: o alerta vira o ponto no canto
           do ícone (mesma linguagem do badge do sino da topbar). -->
      <span class="saldo-rodape__icone">
        <icon-svg
          :name="iconeNome"
          :size="TAMANHO_ICONE"
          :color="iconeCor"
          data-test="saldo-creditos-rodape-icone"
        />
        <span
          v-if="recolhida && alertaDeSaldo"
          class="saldo-rodape__ponto"
          :title="copy.SALDO_BAIXO_TITULO"
          data-test="saldo-creditos-rodape-ponto"
        />
      </span>

      <template v-if="!recolhida">
        <span class="saldo-rodape__rotulo">{{ copy.TITULO }}</span>

        <div
          v-if="loading"
          class="saldo-rodape__valor ml-auto"
          data-test="saldo-creditos-rodape-loading"
        >
          <!-- O `BaseLoadingSkeleton` repassa `class`/atributos para o div INTERNO: o
               alinhamento fica no wrapper e ele recebe só as dimensões. -->
          <BaseLoadingSkeleton width="48px" height="12px" />
        </div>

        <span
          v-else-if="mostrarIndisponivel"
          class="saldo-rodape__valor saldo-rodape__valor--indisponivel ml-auto"
          data-test="saldo-creditos-rodape-indisponivel"
          >{{ copy.RODAPE_INDISPONIVEL }}</span
        >

        <span
          v-else
          class="saldo-rodape__valor ml-auto"
          :class="{
            'saldo-rodape__valor--negativo': negativo,
            'saldo-rodape__valor--baixo': alertaDeSaldo,
          }"
          :data-creditos-exato="creditosRaw"
          data-test="saldo-creditos-rodape-valor"
          >{{ creditosLabel }}</span
        >
        <!-- O triângulo é o segundo canal do "acabando" (a cor é o primeiro); o
             `title`/`aria-label` é o terceiro, para o mouse e o leitor de tela. -->
        <icon-svg
          v-if="alertaDeSaldo && !loading"
          name="warning-status"
          :size="TAMANHO_ICONE_ALERTA"
          :color="corAlerta"
          class="flex-shrink-0"
          role="img"
          :title="copy.SALDO_BAIXO_TITULO"
          :aria-label="copy.SALDO_BAIXO_TITULO"
          data-test="saldo-creditos-rodape-alerta"
        />
      </template>
    </button>
  </div>
</template>

<style lang="sass" scoped>
// `.iv2-chamar-atencao` (o pulso do 402): keyframes + classe viajam com o componente.
@import '@modules/aawz-intelligence-v2/design/efeitos.sass'

// A faixa do rodapé. Ela vive FORA do `.chat-sidebar__corpo` (quem rola), então precisa
// da hairline para as conversas não parecerem continuar por baixo dela — mesma receita
// de divisória de `.chat-sidebar__nav`. O respiro vem das utilitárias (`mt-2 pt-2`), que
// é o que o DS manda para tudo que o Vuetify já cobre.
.saldo-rodape
  // Irmão de um `flex: 1` numa coluna flex: sem isto a faixa encolhe quando a lista
  // cresce, e o rodapé some justamente na conversa longa.
  flex-shrink: 0
  border-top: 1px solid var(--sidebar-warm-border)

  // O rótulo é quem cede espaço; o número nunca trunca — ele é o dado.
  &__rotulo
    overflow: hidden
    text-overflow: ellipsis
    white-space: nowrap

  &__valor
    flex-shrink: 0
    // Dígitos de mesma largura: o número muda a cada leitura e, sem isto, a coluna
    // dança horizontalmente entre um saldo e o seguinte.
    font-variant-numeric: tabular-nums
    // A ênfase da linha é o VALOR — o rótulo fica no peso de repouso de
    // `.ia-sidebar-row`. Os dois tokens são os mesmos do estado ativo daquela receita
    // (--neutral-900 dá 13.66 sobre a superfície da lateral).
    font-weight: 600
    color: var(--neutral-900)

    // Não é dado, é a ausência dele: fica no tom secundário para não competir com um
    // número de verdade na mesma posição.
    &--indisponivel
      font-weight: 500
      color: var(--neutral-700)

    &--negativo
      color: var(--danger-text)

    // Saldo baixo/esgotado: o mesmo âmbar do triângulo ao lado (`--warning-text` é o
    // único da família exportado como custom property; passa AA sobre a lateral).
    &--baixo
      color: var(--warning-text)

  // Ancora o ponto de alerta da régua recolhida.
  &__icone
    position: relative
    display: inline-flex
    flex-shrink: 0

  // O ponto no canto do ícone — receita do badge do sino da topbar
  // (`navegacao-v2/components/topbar-notifications.vue`): absoluto no canto superior
  // direito, redondo, com um filete da cor da superfície para descolar do ícone.
  &__ponto
    position: absolute
    top: -3px
    right: -3px
    width: 8px
    height: 8px
    border-radius: 999px
    background: var(--warning-text)
    border: 2px solid var(--sidebar-warm-bg)
    box-sizing: content-box

// O atalho é um `<button>` vestido de `.ia-sidebar-row`, como as outras linhas da
// lateral — cor, fundo, hover e reset vêm todos de lá. (Enquanto foi `<a>`, este bloco
// precisava vencer o `.v-application a { color }` que o Vuetify injeta do tema; virando
// botão, a disputa deixou de existir e o override saiu junto.)
.saldo-rodape--recolhida
  // Recolhida o botão é um quadrado de 28px: sem isto ele encostava na borda
  // esquerda da régua, fora do eixo dos ícones de cima.
  padding-bottom: 4px

.saldo-rodape__atalho
  // Anel de foco explícito, o mesmo de `.chat-sidebar__retrair`: o `outline` do agente
  // varia por navegador e some sobre a superfície da lateral em alguns deles.
  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px
</style>
